// frontend/src/pages/admin/AdminPollsPanel.js
// Admin → Noticeboard → Polls: every poll (open and closed) with live results,
// who voted what, who hasn't voted, and edit / close / delete.
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const TYPE_LABEL = {
  yes_no: 'Yes / No', multiple_choice: 'Single choice', multi_select: 'Multiple choice',
  short_answer: 'Text response', rating: 'Rating 1–5',
};

// <input type="datetime-local"> works in the browser's local time; the server
// stores UTC. Convert both ways so a deadline typed as 5:00 PM stays 5:00 PM.
function toLocalInput(d) {
  if (!d) return '';
  const t = new Date(d);
  return new Date(t.getTime() - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function fromLocalInput(v) { return v ? new Date(v).toISOString() : null; }

function fmt(d) {
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function asList(resp) {
  let r = resp;
  if (typeof r === 'string') { try { r = JSON.parse(r); } catch (_) { /* plain text */ } }
  return Array.isArray(r) ? r.map(String) : [String(r)];
}

function showAnswer(resp) {
  return asList(resp).map(a => (a === 'yes' ? 'YES' : a === 'no' ? 'NO' : a)).join(', ');
}

export default function AdminPollsPanel({ refreshKey }) {
  const [polls, setPolls] = useState([]);
  const [open, setOpen] = useState(null);        // poll id whose results are shown
  const [results, setResults] = useState(null);  // { responses, not_voted }
  const [editing, setEditing] = useState(null);  // edit form state
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/polls?all=1').then(r => setPolls(r.data.polls || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const loadResults = id => {
    setResults(null);
    api.get(`/polls/${id}/results`)
      .then(r => setResults(r.data))
      .catch(e => toast.error(e?.response?.data?.error || 'Could not load results'));
  };

  const toggle = id => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); loadResults(id);
  };

  const startEdit = p => setEditing({
    id: p.id, question: p.question, response_type: p.response_type,
    options: Array.isArray(p.options) && p.options.length ? [...p.options] : ['', ''],
    deadline: toLocalInput(p.deadline), include_observers: !!p.include_observers,
    publish_results: !!p.publish_results, votes: Number(p.response_count) || 0,
  });

  const save = async () => {
    if (!editing.question.trim()) return toast.error('Question required');
    setBusy(true);
    try {
      const body = {
        question: editing.question,
        deadline: fromLocalInput(editing.deadline),
        include_observers: editing.include_observers,
        publish_results: editing.publish_results,
      };
      if (['multiple_choice', 'multi_select'].includes(editing.response_type) && editing.votes === 0) {
        body.options = editing.options.filter(o => o.trim());
      }
      await api.put(`/polls/${editing.id}`, body);
      toast.success('Poll updated');
      setEditing(null); load();
      if (open === editing.id) loadResults(editing.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Update failed', { duration: 8000 });
    } finally { setBusy(false); }
  };

  const closeNow = async p => {
    if (!window.confirm(`Close "${p.question}" now? Members can no longer vote or change their vote.`)) return;
    try {
      await api.put(`/polls/${p.id}`, { deadline: new Date().toISOString() });
      toast.success('Poll closed'); load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const reopen = async p => {
    const v = window.prompt('New closing date & time (e.g. 2026-10-20 18:00), or leave empty for no deadline:', '');
    if (v === null) return;
    const d = v.trim() ? new Date(v.trim().replace(' ', 'T')) : null;
    if (v.trim() && isNaN(d)) return toast.error('Could not read that date');
    try {
      await api.put(`/polls/${p.id}`, { deadline: d ? d.toISOString() : null, is_active: true });
      toast.success('Poll reopened'); load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const remove = async p => {
    const n = Number(p.response_count) || 0;
    if (!window.confirm(`Delete "${p.question}"${n ? ` and its ${n} vote(s)` : ''}? This can't be undone.`)) return;
    try {
      await api.delete(`/polls/${p.id}`);
      toast.success('Poll deleted');
      if (open === p.id) setOpen(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Delete failed'); }
  };

  if (!polls.length) return null;

  const btn = { fontSize: '0.72rem', padding: '4px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontWeight: 600 };

  return (
    <>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, marginTop: 16 }}>
        📊 Polls ({polls.length})
      </div>
      <div className="card">
        {polls.map((p, i) => {
          const closed = !p.is_active || (p.deadline && new Date(p.deadline) <= new Date());
          const isOpen = open === p.id;
          return (
            <div key={p.id} style={{ padding: '12px 14px', borderBottom: i < polls.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--navy)', marginBottom: 4 }}>{p.question}</div>
                <span style={{ fontSize: '0.65rem', fontWeight: 800, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
                               background: closed ? '#fee2e2' : '#dcfce7', color: closed ? '#b91c1c' : '#15803d' }}>
                  {closed ? 'CLOSED' : 'OPEN'}
                </span>
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{TYPE_LABEL[p.response_type] || p.response_type}</span>
                <span>🗳️ {p.response_count || 0} votes</span>
                <span>{p.deadline ? `⏰ ${closed ? 'Closed' : 'Closes'} ${fmt(p.deadline)}` : '⏰ No deadline'}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button style={btn} onClick={() => toggle(p.id)}>{isOpen ? 'Hide results' : '📊 Results'}</button>
                <button style={btn} onClick={() => startEdit(p)}>✏️ Edit</button>
                {closed
                  ? <button style={btn} onClick={() => reopen(p)}>↺ Reopen</button>
                  : <button style={btn} onClick={() => closeNow(p)}>⛔ Close now</button>}
                <button style={{ ...btn, color: '#b91c1c', borderColor: '#fecaca' }} onClick={() => remove(p)}>🗑 Delete</button>
              </div>

              {isOpen && (
                <div style={{ marginTop: 10, background: '#f8fafc', borderRadius: 10, padding: 10 }}>
                  {!results ? <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Loading…</div> : (() => {
                    const tally = {};
                    results.responses.forEach(r => asList(r.response).forEach(a => { tally[a] = (tally[a] || 0) + 1; }));
                    const choices = p.response_type === 'yes_no' ? ['yes', 'no']
                      : p.response_type === 'rating' ? ['1', '2', '3', '4', '5']
                      : (Array.isArray(p.options) ? p.options : []);
                    const total = results.responses.length;
                    return (
                      <>
                        {p.response_type !== 'short_answer' && choices.map(c => {
                          const n = tally[c] || 0, pct = total ? Math.round(n * 100 / total) : 0;
                          return (
                            <div key={c} style={{ marginBottom: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                                <span>{c === 'yes' ? 'YES' : c === 'no' ? 'NO' : c}</span><strong>{n} ({pct}%)</strong>
                              </div>
                              <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}>
                                <div style={{ width: `${pct}%`, height: 6, background: '#1E3A6E', borderRadius: 3 }} />
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', marginTop: 8 }}>Voted ({total})</div>
                        {total === 0 && <div style={{ fontSize: '0.78rem', color: '#64748b' }}>No votes yet.</div>}
                        {results.responses.map((r, k) => (
                          <div key={k} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderBottom: '1px solid #eef2f7' }}>
                            <span>{r.display_name}</span><strong style={{ textAlign: 'right' }}>{showAnswer(r.response)}</strong>
                          </div>
                        ))}
                        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#b91c1c', marginTop: 10 }}>Not voted ({results.not_voted.length})</div>
                        <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.5 }}>
                          {results.not_voted.length ? results.not_voted.map(m => m.display_name).join(', ') : 'Everyone has voted 🎉'}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)', marginBottom: 16 }}>Edit Poll</div>
            <div className="form-group">
              <label className="form-label">Question</label>
              <textarea className="form-control" rows={2} value={editing.question}
                onChange={e => setEditing(f => ({ ...f, question: e.target.value }))} />
            </div>
            {['multiple_choice', 'multi_select'].includes(editing.response_type) && (
              <div className="form-group">
                <label className="form-label">Choices</label>
                {editing.votes > 0 ? (
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    {editing.options.join(' · ')}<br />
                    <em>{editing.votes} vote(s) already in — choices are locked.</em>
                  </div>
                ) : (
                  <>
                    {editing.options.map((o, k) => (
                      <div key={k} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                        <input className="form-control" value={o}
                          onChange={e => setEditing(f => ({ ...f, options: f.options.map((x, j) => (j === k ? e.target.value : x)) }))} />
                        {editing.options.length > 2 && (
                          <button style={btn} onClick={() => setEditing(f => ({ ...f, options: f.options.filter((_, j) => j !== k) }))}>✕</button>
                        )}
                      </div>
                    ))}
                    <button style={btn} onClick={() => setEditing(f => ({ ...f, options: [...f.options, ''] }))}>+ Add choice</button>
                  </>
                )}
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Closes (leave empty for no deadline)</label>
              <input type="datetime-local" className="form-control" value={editing.deadline}
                onChange={e => setEditing(f => ({ ...f, deadline: e.target.value }))} />
            </div>
            <label style={{ display: 'flex', gap: 8, fontSize: '0.85rem', marginBottom: 8 }}>
              <input type="checkbox" checked={editing.include_observers}
                onChange={e => setEditing(f => ({ ...f, include_observers: e.target.checked }))} />
              Include observers
            </label>
            <label style={{ display: 'flex', gap: 8, fontSize: '0.85rem', marginBottom: 16 }}>
              <input type="checkbox" checked={editing.publish_results}
                onChange={e => setEditing(f => ({ ...f, publish_results: e.target.checked }))} />
              Publish results to members
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
